import React, { useState, useRef, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

const LiveChatPanel = ({ isLive = false, messages = [] }) => {
  const [messageInput, setMessageInput] = useState('');
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef?.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleSendMessage = (e) => {
    e?.preventDefault();
    if (messageInput?.trim()) {
      setMessageInput('');
    }
  };

  const formatTimestamp = (date) => {
    return new Date(date)?.toLocaleTimeString('ru-RU', { 
      hour: '2-digit', 
      minute: '2-digit' 
    });
  };

  return (
    <div className="live-chat-panel bg-card rounded-lg border border-border shadow-soft flex flex-col h-full">
      <div className="chat-header p-4 border-b border-border">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Icon name="MessageCircle" size={20} color="var(--color-foreground)" />
            <h3 className="text-sm font-heading font-semibold text-foreground">
              Чат слушателей
            </h3>
          </div>
          
          {isLive && (
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 bg-success rounded-full animate-pulse" />
              <span className="text-xs font-mono text-success">
                {messages?.length} сообщений
              </span>
            </div>
          )}
        </div>
      </div>
      <div className="chat-messages flex-1 overflow-y-auto p-4 space-y-4">
        {!isLive && (
          <div className="empty-state flex flex-col items-center justify-center h-full text-center p-6">
            <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center mb-4">
              <Icon name="MessageCircle" size={32} color="var(--color-muted-foreground)" />
            </div>
            <p className="text-sm text-muted-foreground">
              Начните трансляцию, чтобы видеть сообщения слушателей
            </p>
          </div>
        )}

        {isLive && messages?.length === 0 && (
          <div className="empty-state flex flex-col items-center justify-center h-full text-center p-6">
            <p className="text-sm text-muted-foreground">
              Пока нет сообщений. Будьте первым!
            </p>
          </div>
        )}

        {messages?.map((message) => (
          <div key={message?.id} className="chat-message flex gap-3">
            <div className="message-avatar flex-shrink-0">
              <Image
                src={message?.avatar}
                alt={message?.avatarAlt}
                className="w-8 h-8 rounded-full object-cover"
              />
            </div>
            
            <div className="message-content flex-1 min-w-0">
              <div className="flex items-baseline gap-2 mb-1">
                <span className="text-sm font-heading font-medium text-foreground truncate">
                  {message?.username}
                </span>
                <span className="text-xs font-mono text-muted-foreground flex-shrink-0">
                  {formatTimestamp(message?.timestamp)}
                </span>
              </div>
              
              <p className="text-sm text-foreground break-words">
                {message?.content}
              </p>

              {message?.reaction && (
                <div className="message-reaction inline-flex items-center gap-1 mt-2 px-2 py-1 bg-muted rounded-full">
                  <span className="text-base">{message?.reaction}</span>
                </div>
              )}
            </div>
          </div>
        ))}
        
        <div ref={messagesEndRef} />
      </div>
      <div className="chat-input p-4 border-t border-border">
        <form onSubmit={handleSendMessage} className="flex gap-2">
          <Input
            type="text"
            placeholder={isLive ? "Написать сообщение..." : "Чат доступен во время эфира"}
            value={messageInput}
            onChange={(e) => setMessageInput(e?.target?.value)}
            disabled={!isLive}
            className="flex-1"
          />
          
          <Button
            type="submit"
            variant="default"
            size="default"
            iconName="Send"
            disabled={!isLive || !messageInput?.trim()}
          />
        </form>
      </div>
    </div>
  );
};

export default LiveChatPanel;